import Element from '../../../../Prototype/UI/Element';
import { IUIElementLayout } from '../../../../Prototype/UI/UIItem';
import AnimationComponent from '../../../../Component/Animation';
import GameObject, { GameObjectEvent } from '../../../../GameObject/GameObject';
import UITextComponent from '../../../../Component/UI/Text';
import UIImageComponent from '../../../../Component/UI/Image';
import Game from '../../../../Game';
import CleanupManager from '../../../../Utility/CleanupManager';
import { UIActions } from '../../../../State/UI/UIActions';
import { applyDefaultButtonAnimation } from '../../../../System/UI/UIHelper';
import StateComponent from '../../../../Component/State';
import { IGameState } from '../../../../State/Game/IGameState';
import { tVector4 } from '../../../../Utility/ArrayHelper';
import ElementComponent from '../../../../Component/UI/Element';
import Renderable from '../../../../Component/Renderable';
import DateHelper from '../../../../Utility/DateHelper';
import { questSlotsCount, questTimerDuration } from '../../../../Constants/Quests';
import QuestTimer from '../../../../System/Timer/Timers/QuestTimer';
import Scene from '../../../../Scene/Scene';
import { StartAnimation } from '../../../../System/Animation/AnimationHelper';
import { AnimationClipEvent } from '../../../../System/Animation/AnimationClip';
import { IPlayerStateQuestData } from '../../../../State/Player/IPlayerState';
import { PlayerActions } from '../../../../State/Player/PlayerActions';
import Timer from '../../../../System/Timer/Timers/Timer';
import { PopupID } from '../../../../State/UI/PopupID';
import { QuestStateHelper } from 'State/Player/Quests/QuestStateHelper';
import { EventStateHelper } from 'State/Events/EventStateHelper';
import { EventType } from 'Utility/Helpers/Events/EventType';

enum QuestButtonStatus {
    ALL_IN_PROGRESS = 'allInProgress',
    SOME_COMPLETED = 'someCompleted',
    ON_COOLDOWN = 'onCooldown',
}
interface IQuestButtonSettings {
    buttonTexture: string;
    buttonTextAnchor: tVector4;
    buttonTextStyle: string;
}
const buttonSettings: Record<QuestButtonStatus, IQuestButtonSettings> = {
    [QuestButtonStatus.ALL_IN_PROGRESS]: {
        buttonTexture: 'mission_button_base',
        buttonTextAnchor: [0.61, 0.45, 0.61, 0.45],
        buttonTextStyle: 'quests.button.main_text',
    },
    [QuestButtonStatus.SOME_COMPLETED]: {
        buttonTexture: 'mission_button_reward',
        buttonTextAnchor: [0.61, 0.45, 0.61, 0.45],
        buttonTextStyle: 'quests.button.completed',
    },
    [QuestButtonStatus.ON_COOLDOWN]: {
        buttonTexture: 'mission_button_base',
        buttonTextAnchor: [0.61, 0.27, 0.61, 0.27],
        buttonTextStyle: 'quests.button.main_text',
    },
};
const eventButtonSettings: Record<QuestButtonStatus, IQuestButtonSettings> = {
    [QuestButtonStatus.ALL_IN_PROGRESS]: {
        buttonTexture: 'UI_Christmas_mission_button',
        buttonTextAnchor: [0.61, 0.59, 0.61, 0.59],
        buttonTextStyle: 'events.xmas.main_text',
    },
    [QuestButtonStatus.SOME_COMPLETED]: {
        buttonTexture: 'UI_Christmas_mission_button2',
        buttonTextAnchor: [0.61, 0.59, 0.61, 0.59],
        buttonTextStyle: 'events.xmas.main_text',
    },
    [QuestButtonStatus.ON_COOLDOWN]: {
        buttonTexture: 'UI_Christmas_mission_button',
        buttonTextAnchor: [0.61, 0.45, 0.61, 0.45],
        buttonTextStyle: 'events.xmas.main_text',
    },
};
export const ShowQuestProgressEvent: string = 'showQuestProgress';

export default class QuestsButton extends Element {
    private readonly cleanupManager: CleanupManager = new CleanupManager();

    private status: QuestButtonStatus;
    private progressUpdates: IPlayerStateQuestData[] = [];
    private startedProgressAnimation: boolean = false;
    private readonly timers: Timer[] = [];
    private currentTimerIndex: number;

    public create(params: { name?: string; layout?: Partial<IUIElementLayout>; scene: Scene }): void {
        const hasXmasEvent = EventStateHelper.getEventState(Game.store.getState(), EventType.XMAS_2021);
        // const hasXmasEvent = false;
        super.create({
            name: params.name,
            layout: {
                anchors: [0.5, 0.5, 0.5, 0.5],
                pivot: [0.5, 0.5],
                offsets: [0, 0, 0, 0],
                scale: [1, 1],
                ...params.layout,
            },
            components: [
                {
                    type: 'image',
                    data: {
                        cacheKey: hasXmasEvent ? 'UI_Christmas_mission_button_frame' : 'mission_button_back',
                    },
                },
            ],
            children: [
                {
                    name: 'update_container',
                    layout: {
                        anchors: hasXmasEvent ? [0.12, 0.65, 0.12, 0.65] : [0.17, 0.5, 0.17, 0.5],
                        pivot: [0, 0.5],
                        offsets: [0, 0, 0, 0],
                        scale: [1, 1],
                        constrains: {
                            heightMin: 100,
                            heightMax: 100,
                            widthMin: 300,
                            widthMax: 300,
                        },
                    },
                    components: [
                        {
                            type: 'nineslice',
                            data: {
                                cacheKey: hasXmasEvent ? 'UI_Christmas_mission_notification_panel' : 'mission_button_notify',
                                offset: [10, 30, 10, 30],
                            },
                        },
                        {
                            type: 'mask',
                            data: {
                                offset: [0, 0, 0, 0],
                            },
                        },
                    ],
                },
                {
                    name: 'button',
                    layout: {
                        anchors: [0.5, 0.5, 0.5, 0.5],
                        pivot: [0.5, 0.5],
                        offsets: [0, 0, 0, 0],
                        scale: [1, 1],
                    },
                    components: [
                        {
                            type: 'image',
                            data: {
                                cacheKey: hasXmasEvent ? 'UI_Christmas_mission_button' : 'mission_button_base',
                            },
                        },
                        {
                            type: 'input',
                            data: {
                                enabled: true,
                                buttonMode: true,
                            },
                        },
                    ],
                    children: [
                        {
                            name: 'main_text',
                            layout: {
                                anchors: [0.61, 0.45, 0.61, 0.45],
                                pivot: [0.5, 0.5],
                                offsets: [0, 0, 0, 0],
                                scale: [1, 1],
                            },
                            components: [
                                {
                                    type: 'text',
                                    data: {
                                        text: '',
                                        style: 'quests.button.main_text',
                                        bounds: [210, 60],
                                    },
                                },
                            ],
                            children: [
                                {
                                    name: 'timer_container',
                                    layout: {
                                        anchors: [0.5, 1, 0.5, 1],
                                        pivot: [0.5, 0],
                                        offsets: [0, 0, 0, 0],
                                        scale: [1, 1],
                                    },
                                    components: [
                                        {
                                            type: 'image',
                                            data: {
                                                cacheKey: hasXmasEvent ? 'UI_Christmas_mission_timer_back' : 'mission_timer_back',
                                            },
                                        },
                                    ],
                                    children: [
                                        {
                                            name: 'timer_icon',
                                            layout: {
                                                anchors: [0.15, 0.5, 0.15, 0.5],
                                                pivot: [0.5, 0.5],
                                                offsets: [0, 0, 0, 0],
                                                scale: [1, 1],
                                            },
                                            components: [
                                                {
                                                    type: 'image',
                                                    data: {
                                                        cacheKey: 'mission_timer_icon',
                                                    },
                                                },
                                            ],
                                            children: [],
                                        },
                                        {
                                            name: 'timer_text',
                                            layout: {
                                                anchors: [0.6, 0.5, 0.6, 0.5],
                                                pivot: [0.5, 0.5],
                                                offsets: [0, 0, 0, 0],
                                                scale: [1, 1],
                                            },
                                            components: [
                                                {
                                                    type: 'text',
                                                    data: {
                                                        text: '99:99:99',
                                                        style: hasXmasEvent ? 'events.xmas.timer' : 'quests.button.timer',
                                                    },
                                                },
                                            ],
                                            children: [],
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
            ],
            ...params,
        });
        this.addComponent(AnimationComponent);

        this.updateContainer.addComponent(AnimationComponent);
        for (let i = 0; i < questSlotsCount; i++) {
            this.timers.push(new QuestTimer(i));
            this.timers[i].complete = () => {
                PlayerActions.refreshQuestAfterCooldown(i);
            };
            this.cleanupManager.add(() => {
                this.timers[i].stop();
            });
        }

        const button = this.button;
        button.on(GameObjectEvent.SELECTED, this.onClick);
        applyDefaultButtonAnimation(button);
        this.cleanupManager.add(() => {
            button.off(GameObjectEvent.SELECTED, this.onClick);
        });

        this.addComponent(StateComponent, {
            handleStateChanged: this.handleStateChanged,
        });

        this.handleStateChanged(Game.store.getState());

        params.scene.on(ShowQuestProgressEvent, this.queueQuestProgressAnimation);
        this.cleanupManager.add(() => {
            params.scene.off(ShowQuestProgressEvent, this.queueQuestProgressAnimation);
        });
    }
    private readonly onClick = () => {
        UIActions.showPopup(PopupID.QUEST_MENU, {});
    };
    private readonly queueQuestProgressAnimation = (questData: IPlayerStateQuestData) => {
        this.progressUpdates.push(questData);
        this.startAnimationIfNeeded();
    };
    private readonly handleStateChanged = (state: IGameState) => {
        this.updateStatus(state);
        const quests = state.playerState.quests;
        const activeQuests = quests.filter(quest => quest.cooldownStartedAt === null);
        this.updateQuestPanelText(
            activeQuests.length,
            quests.length,
            QuestStateHelper.canCompleteAnyQuest(state.playerState)
        );
        this.startAnimationIfNeeded();
    };
    private readonly updateTimerText = (state: IGameState) => {
        try {
            const timerText = this.timerText;
            if (this.status !== QuestButtonStatus.ON_COOLDOWN || timerText?.destroyed) return;
            const newText: string = DateHelper.timeTo(
                DateHelper.now,
                new Date(this.timers[this.currentTimerIndex].getDate(state).getTime() + questTimerDuration),
                'hh:mm:ss'
            );

            timerText.getComponent(UITextComponent).text = newText;
        } catch (error) {
            console.error(error);
        }
    };
    private updateStatus(state: IGameState) {
        // Get the current status of the button
        const status = this.getStatus(state);
        // Get the index of the quest with the shortest cooldown (the one to display the timer for)
        const shortestCooldownIndex =
            status === QuestButtonStatus.ON_COOLDOWN ? this.getIndexOfQuestWithShortestCooldown(state) : undefined;

        // If status is upto date and the timer to display hasn't changed, do nothing
        if (status === this.status && this.currentTimerIndex === shortestCooldownIndex) return;

        // Update status
        this.status = status;
        const hasXmasEvent = EventStateHelper.getEventState(Game.store.getState(), EventType.XMAS_2021);
        // const hasXmasEvent = false;
        const currentBtnSettings = hasXmasEvent ? eventButtonSettings : buttonSettings;

        // Set text
        const text = this.text;
        text.getComponent(ElementComponent).anchors = currentBtnSettings[status].buttonTextAnchor;
        text.getComponent(UITextComponent).style = Game.style.getStyle(currentBtnSettings[status].buttonTextStyle);

        // Set button texture
        this.button.getComponent(UIImageComponent).texture = currentBtnSettings[status].buttonTexture;

        this.timerContainer.getComponent(Renderable).visible = status === QuestButtonStatus.ON_COOLDOWN;

        // If on cooldown, start timer
        if (status === QuestButtonStatus.ON_COOLDOWN) {
            this.currentTimerIndex = shortestCooldownIndex;
            this.updateTimerText(state);
            this.timers.forEach((timer, index) => {
                timer.onUpdate = index === this.currentTimerIndex ? this.updateTimerText : () => {};
            });
        }
    }
    private updateQuestPanelText(active: number, total: number, canClaimReward: boolean) {
        const text = this.text;
        const missionLangStr: string = Game.locale.getText('Quests');
        if (canClaimReward) {
            text.getComponent(UITextComponent).text = `${Game.locale.getText('ClaimReward')}`;
        } else if (active === total) {
            text.getComponent(UITextComponent).text = `${missionLangStr}`;
        } else {
            text.getComponent(UITextComponent).text = `${missionLangStr} (${active}/${total})`;
        }
    }
    private getStatus(state: IGameState): QuestButtonStatus {
        if (QuestStateHelper.canCompleteAnyQuest(state.playerState)) {
            return QuestButtonStatus.SOME_COMPLETED;
        }
        if (state.playerState.quests.some(quest => quest.cooldownStartedAt !== null)) {
            return QuestButtonStatus.ON_COOLDOWN;
        }
        return QuestButtonStatus.ALL_IN_PROGRESS;
    }
    private getIndexOfQuestWithShortestCooldown(state: IGameState): number {
        const questsOnCooldown = state.playerState.quests.filter(quest => quest.cooldownStartedAt !== null);

        questsOnCooldown.sort((a, b) => a.cooldownStartedAt.getTime() - b.cooldownStartedAt.getTime());

        return state.playerState.quests.indexOf(questsOnCooldown[0]);
    }
    private startAnimationIfNeeded() {
        if (!this.destroyed && !this.startedProgressAnimation && this.progressUpdates.length > 0) {
            const getNextUpdate = () => {
                return this.progressUpdates.pop();
            };
            this.startedProgressAnimation = true;
            StartAnimation(this.updateContainer, 'UpdateQuestProgress', {
                getNextUpdate,
            })?.once(AnimationClipEvent.DESTROY, () => {
                this.startedProgressAnimation = false;
                this.startAnimationIfNeeded();
            });
        }
    }

    public destroy() {
        this.cleanupManager.cleanUpAll();
        super.destroy();
    }

    private get timerText(): GameObject {
        return this.find('button/main_text/timer_container/timer_text');
    }
    public get button(): GameObject {
        return this.find('button');
    }
    private get text(): GameObject {
        return this.find('button/main_text');
    }
    private get timerContainer(): GameObject {
        return this.find('button/main_text/timer_container');
    }
    private get updateContainer(): GameObject {
        return this.find('update_container');
    }
}
